import React from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  IconButton,
  HStack,
  Icon,
  Badge,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { CheckIcon, EditIcon } from "@chakra-ui/icons";
import { FiFolder, FiShare2 } from "react-icons/fi";
import DeleteButtonWithConfirmation from "./DeleteConfirmation";

/**
 * Section shell: use `variant` to separate “browse folders”, “nested folders”, and “forms”.
 * - default: neutral blue-gray header (generic)
 * - browse: teal accent — top-level folder grids
 * - nested: indigo accent — subfolder grids inside a parent
 * - ledger: purple accent — optional emphasis for mixed content
 */
export function DashboardCategoryPanel({
  title,
  description,
  children,
  variant = "default",
}) {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const boxBg = useColorModeValue("white", "gray.700");
  const headerDefault = useColorModeValue("gray.100", "gray.700");
  const headerBrowse = useColorModeValue("teal.50", "rgba(20, 184, 166, 0.15)");
  const headerNested = useColorModeValue("indigo.50", "rgba(79, 70, 229, 0.18)");
  const headerLedger = useColorModeValue("purple.50", "rgba(147, 51, 234, 0.15)");
  const headingDefault = useColorModeValue("blue.600", "blue.300");
  const headingBrowse = useColorModeValue("teal.700", "teal.200");
  const headingNested = useColorModeValue("indigo.700", "indigo.200");
  const headingLedger = useColorModeValue("purple.700", "purple.200");
  const sectionBodyBg = useColorModeValue("gray.50", "gray.900");
  const sectionBodyBrowse = useColorModeValue("gray.50", "gray.900");
  const sectionBodyNested = useColorModeValue("gray.50", "rgba(15, 23, 42, 0.5)");
  const leftAccent = useColorModeValue("teal.400", "teal.300");
  const leftAccentNested = useColorModeValue("indigo.400", "indigo.300");
  const leftAccentLedger = useColorModeValue("purple.400", "purple.300");

  const headerStripBg =
    variant === "browse"
      ? headerBrowse
      : variant === "nested"
        ? headerNested
        : variant === "ledger"
          ? headerLedger
          : headerDefault;
  const headingColor =
    variant === "browse"
      ? headingBrowse
      : variant === "nested"
        ? headingNested
        : variant === "ledger"
          ? headingLedger
          : headingDefault;
  const bodyBg =
    variant === "nested" ? sectionBodyNested : variant === "browse" ? sectionBodyBrowse : sectionBodyBg;
  const leftBar =
    variant === "browse"
      ? leftAccent
      : variant === "nested"
        ? leftAccentNested
        : variant === "ledger"
          ? leftAccentLedger
          : undefined;

  return (
    <Box
      w="full"
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      boxShadow="md"
      bg={boxBg}
      borderLeftWidth={leftBar ? "4px" : "1px"}
      borderLeftColor={leftBar ?? borderColor}
    >
      <Box
        px={{ base: 4, md: 6 }}
        py={4}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bg={headerStripBg}
      >
        <Heading
          size="sm"
          textAlign="left"
          color={headingColor}
          fontWeight="bold"
        >
          {title}
        </Heading>
        {description && (
          <Text fontSize="sm" color="gray.500" mt={1} textAlign="left">
            {description}
          </Text>
        )}
      </Box>
      <Box p={{ base: 4, md: 5 }} bg={bodyBg}>
        {children}
      </Box>
    </Box>
  );
}

/**
 * Distinct “create / add” panels — dashed vs solid so Add folder vs Add note never look the same.
 * kind: "folder" | "note" | "file" | "splitwise"
 */
export function DashboardActionShell({
  title,
  subtitle,
  badgeLabel,
  children,
  kind = "folder",
  ...boxProps
}) {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const folderBg = useColorModeValue("green.50", "rgba(34, 197, 94, 0.12)");
  const folderHeader = useColorModeValue("green.100", "rgba(22, 163, 74, 0.25)");
  const noteBg = useColorModeValue("blue.50", "rgba(59, 130, 246, 0.12)");
  const noteHeader = useColorModeValue("blue.100", "rgba(37, 99, 235, 0.25)");
  const fileBg = useColorModeValue("orange.50", "rgba(249, 115, 22, 0.12)");
  const fileHeader = useColorModeValue("orange.100", "rgba(234, 88, 12, 0.22)");
  const swBg = useColorModeValue("teal.50", "rgba(20, 184, 166, 0.14)");
  const swHeader = useColorModeValue("teal.100", "rgba(13, 148, 136, 0.28)");

  const scheme =
    kind === "note"
      ? { bg: noteBg, head: noteHeader, dash: "blue.300", badge: "blue" }
      : kind === "file"
        ? { bg: fileBg, head: fileHeader, dash: "orange.300", badge: "orange" }
        : kind === "splitwise"
          ? { bg: swBg, head: swHeader, dash: "teal.300", badge: "teal" }
          : { bg: folderBg, head: folderHeader, dash: "green.300", badge: "green" };

  return (
    <Box
      w="full"
      flex={1}
      borderRadius="xl"
      borderWidth="2px"
      borderStyle="dashed"
      borderColor={scheme.dash}
      bg={scheme.bg}
      overflow="hidden"
      boxShadow="md"
      {...boxProps}
    >
      <Box px={{ base: 4, md: 5 }} py={3} bg={scheme.head} borderBottomWidth="1px" borderColor={borderColor}>
        {badgeLabel && (
          <Badge colorScheme={scheme.badge} borderRadius="md" fontSize="xs" px={2} py={0.5}>
            {badgeLabel}
          </Badge>
        )}
        <Heading size="md" mt={badgeLabel ? 1 : 0} color="gray.800" sx={{ _dark: { color: "gray.100" } }}>
          {title}
        </Heading>
        {subtitle && (
          <Text fontSize="sm" color="gray.600" mt={1} sx={{ _dark: { color: "gray.400" } }}>
            {subtitle}
          </Text>
        )}
      </Box>
      <Box p={{ base: 4, md: 5 }}>{children}</Box>
    </Box>
  );
}

/**
 * Folder card — same visual language as Portfolio category cards, without balance row.
 */
export function DashboardFolderCard({
  variant = "teal",
  itemId,
  title: titleText,
  isEditingThisRow,
  editedTitle,
  onEditedTitleChange,
  onSelect,
  onSaveEdit,
  onStartEdit,
  onDelete,
  deleteAriaLabel = "Delete folder",
  saveAriaLabel = "Save name",
  editAriaLabel = "Edit folder",
  borderColor: borderColorProp,
  textColor: textColorProp,
  inputBgColor: inputBgProp,
  categoryRowBg: rowBgProp,
  categoryRowHoverBg: rowHoverProp,
  categoryIconBg: iconBgProp,
  collaborationShared = false,
  collaborationCanInvite = false,
  showShareButton = false,
  onShareClick,
}) {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const defaultTextColor = useColorModeValue("gray.800", "gray.100");
  const defaultInputBg = useColorModeValue("white", "gray.800");
  const defaultCategoryRowBg = useColorModeValue("white", "gray.800");
  const defaultCategoryRowHoverBg = useColorModeValue("gray.50", "gray.700");
  const defaultCategoryIconBg = useColorModeValue("teal.50", "teal.900");
  const iconPlateBg = useColorModeValue("white", "gray.700");

  const textColor = textColorProp ?? defaultTextColor;
  const inputBgColor = inputBgProp ?? defaultInputBg;
  const categoryRowBg = rowBgProp ?? defaultCategoryRowBg;
  const categoryRowHoverBg = rowHoverProp ?? defaultCategoryRowHoverBg;
  const categoryIconBg = iconBgProp ?? defaultCategoryIconBg;
  const isBlue = variant === "blue";
  const borderAccent = isBlue ? "blue.400" : "teal.400";
  const hoverBorder = isBlue ? "blue.300" : "teal.300";
  const folderColor = isBlue ? "blue.500" : "teal.500";

  return (
    <Box
      role="group"
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColorProp ?? borderColor}
      borderLeftWidth="4px"
      borderLeftColor={borderAccent}
      bg={categoryRowBg}
      cursor="pointer"
      overflow="hidden"
      boxShadow="sm"
      transition="all 0.2s ease"
      onClick={onSelect}
      _hover={{
        bg: categoryRowHoverBg,
        boxShadow: "md",
        borderColor: hoverBorder,
        transform: "translateY(-2px)",
      }}
    >
      <Flex
        align="center"
        gap={3}
        px={{ base: 3, md: 4 }}
        py={3}
        bg={categoryIconBg}
        borderBottomWidth="1px"
        borderColor={borderColorProp ?? borderColor}
      >
        <Flex
          align="center"
          justify="center"
          w="40px"
          h="40px"
          borderRadius="lg"
          bg={iconPlateBg}
          flexShrink={0}
          boxShadow="sm"
        >
          <Icon as={FiFolder} w={5} h={5} color={folderColor} />
        </Flex>
        <Box flex="1" minW={0} textAlign="left">
          {isEditingThisRow ? (
            <Input
              value={editedTitle}
              onChange={(e) => {
                e.stopPropagation();
                onEditedTitleChange(e.target.value);
              }}
              size="sm"
              variant="outline"
              bg={inputBgColor}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <Flex align="center" gap={2} minW={0} wrap="wrap">
              <Text
                fontWeight="bold"
                fontSize={{ base: "sm", md: "md" }}
                noOfLines={2}
                color={textColor}
                title={titleText}
                lineHeight="short"
                flex="1"
                minW={0}
              >
                {titleText}
              </Text>
              {collaborationShared ? (
                <Badge colorScheme="purple" fontSize="10px" borderRadius="md" flexShrink={0}>
                  Shared with you
                </Badge>
              ) : null}
            </Flex>
          )}
        </Box>
      </Flex>
      <Flex
        align="center"
        justify="flex-end"
        gap={3}
        flexWrap="wrap"
        px={{ base: 3, md: 4 }}
        py={3}
      >
        <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
          {showShareButton &&
          onShareClick &&
          !(collaborationShared && !collaborationCanInvite) &&
          !isEditingThisRow ? (
            <Tooltip label="Invite others — same folder and updates for everyone" hasArrow openDelay={400}>
              <IconButton
                icon={<Icon as={FiShare2} />}
                size="sm"
                variant="ghost"
                colorScheme="purple"
                aria-label="Share folder"
                onClick={(e) => {
                  e.stopPropagation();
                  onShareClick();
                }}
              />
            </Tooltip>
          ) : null}
          {isEditingThisRow ? (
            <IconButton
              icon={<CheckIcon />}
              size="sm"
              variant="ghost"
              aria-label={saveAriaLabel}
              onClick={(e) => {
                e.stopPropagation();
                onSaveEdit();
              }}
            />
          ) : (
            <IconButton
              icon={<EditIcon />}
              size="sm"
              variant="ghost"
              aria-label={editAriaLabel}
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit();
              }}
            />
          )}
          <DeleteButtonWithConfirmation
            onDelete={onDelete}
            itemId={itemId}
            size="sm"
            colorScheme="red"
            ariaLabel={deleteAriaLabel}
          />
        </HStack>
      </Flex>
    </Box>
  );
}
